import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Building2,
    Flag,
    Search,
    Filter,
    CheckCircle,
    XCircle,
    AlertTriangle,
    ExternalLink,
    ShieldAlert
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    pending: 'bg-yellow-100 text-yellow-800',
    reviewed: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-800',
};

export default function AdminReports() {
    const { getAuthHeader } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [selectedReport, setSelectedReport] = useState(null);
    const [actionNotes, setActionNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'resolved', 'dismissed', 'banned'

    useEffect(() => {
        fetchReports();
    }, [filter]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/admin/reports`, {
                headers: getAuthHeader(),
                params: { status: filter === 'all' ? undefined : filter }
            });
            setReports(response.data.reports);
        } catch (error) {
            console.error('Failed to fetch reports:', error);
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const handleResolve = async () => {
        if (!selectedReport || !confirmAction) return;

        setActionLoading(true);
        try {
            await axios.put(`${API_URL}/api/admin/reports/${selectedReport.id}/resolve`, {
                action: confirmAction,
                notes: actionNotes
            }, { headers: getAuthHeader() });

            toast.success(`Report marked as ${confirmAction}`);
            setReports(prev => prev.filter(r => r.id !== selectedReport.id));
            setSelectedReport(null);
            setConfirmAction(null);
            setActionNotes('');
        } catch (error) {
            toast.error('Failed to update report');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUnban = async (propertyId) => {
        if (!window.confirm('Are you sure you want to unban this property?')) return;

        try {
            await axios.put(`${API_URL}/api/admin/properties/${propertyId}/unban`, {}, {
                headers: getAuthHeader()
            });
            toast.success('Property unbanned successfully');
        } catch (error) {
            console.error('Unban failed:', error);
            toast.error('Failed to unban property');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="p-6 lg:p-8 space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-navy-900">Reports Management</h1>
                    <p className="text-navy-600">Review and resolve reported content</p>
                </div>

                <div className="flex gap-2 bg-white p-1 rounded-lg border border-navy-200">
                    {['pending', 'resolved', 'dismissed', 'all'].map((status) => (
                        <button
                            key={status}
                            onClick={() => setFilter(status)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === status
                                ? 'bg-navy-900 text-white'
                                : 'text-navy-600 hover:bg-navy-50'
                                }`}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid gap-6">
                {loading ? (
                    <div className="text-center py-12">Loading reports...</div>
                ) : reports.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl border border-navy-100">
                        <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                        <h3 className="text-lg font-medium text-navy-900">All caught up!</h3>
                        <p className="text-navy-500">No reports found with status "{filter}"</p>
                    </div>
                ) : (
                    reports.map((report) => (
                        <div
                            key={report.id}
                            className="bg-white rounded-xl border border-navy-100 p-6 shadow-sm hover:shadow-md transition-shadow"
                        >
                            <div className="flex flex-col lg:flex-row justify-between gap-6">
                                <div className="flex-1 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Badge variant="outline" className={STATUS_COLORS[report.status]}>
                                                {report.status.toUpperCase()}
                                            </Badge>
                                            <Badge variant="secondary" className="capitalize">
                                                {report.reason.replace('_', ' ')}
                                            </Badge>
                                            <span className="text-sm text-navy-400">
                                                {formatDate(report.created_at)}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="font-semibold text-navy-900 flex items-center gap-2">
                                            Reported {report.target_type}: {report.target_name}
                                            {/* Link to target based on type */}
                                            {report.target_type === 'property' && (
                                                <a
                                                    href={`/agency/view/property/${report.target_id}`} // Assuming route structure, though admin likely can view regardless
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sky-500 hover:text-sky-600"
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            )}
                                        </h3>
                                        <p className="text-navy-600 mt-2 bg-navy-50 p-3 rounded-lg border border-navy-100">
                                            "{report.description}"
                                        </p>
                                        <p className="text-sm text-navy-400 mt-2">
                                            Reported by: {report.reporter_info?.name || report.reporter_info?.company_name || 'Anonymous'} ({report.reporter_type})
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 min-w-[150px]">
                                    {report.status === 'pending' ? (
                                        <>
                                            <Button
                                                onClick={() => { setSelectedReport(report); setConfirmAction('resolved'); }}
                                                className="bg-navy-900 border-navy-900 hover:bg-navy-800"
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Resolve
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={() => { setSelectedReport(report); setConfirmAction('dismissed'); }}
                                            >
                                                <XCircle className="w-4 h-4 mr-2" />
                                                Dismiss
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                onClick={() => { setSelectedReport(report); setConfirmAction('banned'); }}
                                                className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200"
                                            >
                                                <ShieldAlert className="w-4 h-4 mr-2" />
                                                Ban Target
                                            </Button>
                                        </>
                                    ) : (
                                        /* Unban Option for Banned Property Reports */
                                        report.status === 'banned' && report.target_type === 'property' && (
                                            <Button
                                                variant="outline"
                                                className="text-green-600 border-green-200 hover:bg-green-50"
                                                onClick={() => handleUnban(report.target_id)}
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Unban Property
                                            </Button>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Action Dialog */}
            <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="capitalize">
                            {confirmAction === 'banned' ? 'Ban Target' : `Mark as ${confirmAction}`}
                        </DialogTitle>
                        <DialogDescription>
                            {confirmAction === 'banned'
                                ? 'This will deactivate the associated agency/property immediately. Are you sure?'
                                : 'Add any administrative notes for this action.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Admin Notes</Label>
                            <Textarea
                                placeholder="Internal notes about this decision..."
                                value={actionNotes}
                                onChange={e => setActionNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
                        <Button
                            variant={confirmAction === 'banned' ? 'destructive' : 'default'}
                            onClick={handleResolve}
                            disabled={actionLoading}
                        >
                            {actionLoading ? 'Processing...' : 'Confirm'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
